import { type GA4PageNameType } from '@/ga4types'
import { formatDate } from '@/shared-utils/date-helper'
import { HOTEL_SEARCH_DATA, persistSearchData } from '@/shared-utils/local-forage-helper'
import packagesSearch from '@/shared-utils/packages-search'
import type { ChildrenAges } from '@pcln/traveler-selection'
import queryString from 'query-string'
import { CAR, FLIGHT } from '../../Packages/SearchForm/TripTypes'
import { PromoDeal, STAY } from '../constants'
import fireHotelSubmitEvent from '../ga4'
import type {
  FilterCategoryHotel,
  FilterValueHotel,
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
} from '../types'

const initialFilterState = {
  amenities: new Set<FilterValueHotel<'amenities'>>(),
  hotelStarLevel: new Set<FilterValueHotel<'hotelStarLevel'>>(),
  guestRating: new Set<FilterValueHotel<'guestRating'>>(),
  propertyTypes: new Set<FilterValueHotel<'propertyTypes'>>(),
} as const

export const initialState: HOTEL_FORM_STATE_TYPE = {
  endLocation: null,
  startLocation: null,
  travelers: {
    adults: 2,
    children: 0,
    lapInfants: 0,
    childrenAges: {},
    rooms: 1,
  },
  startDate: '',
  endDate: '',
  tripType: STAY,
  mapView: false,
  selectedTopFilters: { ...initialFilterState },
}

export const fields = [
  'startLocation',
  'endLocation',
  'startDate',
  'endDate',
  'travelers',
  'tripType',
] as const

const getHotelMultiOccParam = ({
  adultCount,
  childrenAges,
}: {
  adultCount: number
  childrenAges: ChildrenAges
}) => {
  let childrenCount = 0
  let childrenAgesParam = ''

  if (Array.isArray(childrenAges)) {
    childrenCount = childrenAges.length
    childrenAgesParam = childrenAges.join(',')
  } else if (typeof childrenAges === 'object') {
    const ages = Object.values(childrenAges)
    childrenCount = ages.length
    childrenAgesParam = ages.join(',')
  }

  return `/adults/${adultCount}${childrenCount > 0 ? `/children/${childrenAgesParam}` : ''}`
}

const baseURL = 'https://findgroupdeals.hotelplanner.com/Priceline.cfm?City='
const basePartnerURL = 'https://priceline.partners.engine.com/p/create-a-trip?City='

export const groupDealsURL = ({
  location,
  startDate,
  endDate,
  isPartnersURL,
}: {
  location: string
  startDate: string
  endDate: string
  isPartnersURL?: boolean
}) => {
  return `${isPartnersURL ? basePartnerURL : baseURL}${encodeURIComponent(
    location,
  )}&CheckIn=${encodeURIComponent(formatDate({ dateStr: startDate }))}&CheckOut=${encodeURIComponent(formatDate({ dateStr: endDate }))}`
}

const dealTypeUrlMap = {
  [PromoDeal.EXPRESS_DEAL]: '/sopq',
  [PromoDeal.NHA]: '/nha',
}

function handleFilterParams(filterType: FilterCategoryHotel, filters: string[]) {
  if (filterType === 'amenities') {
    return `amenities=${encodeURIComponent(filters.join(','))}`
  }
  if (filterType === 'guestRating') {
    return `minGuestRating=${encodeURIComponent(filters.join(','))}`
  }
  if (filterType === 'hotelStarLevel') {
    const starsTonumbers = filters.map(Number)
    const minStarNum = Math.min(...starsTonumbers)
    const stars = ['1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5']
    const starsString = stars.slice(minStarNum * 2 - 2)
    return `starratings=${encodeURIComponent(starsString.join(','))}`
  }
  if (filterType === 'propertyTypes') {
    return `propertytypes=${encodeURIComponent(filters.join(','))}`
  }
}

export function formatHotelsURL({
  endLocation,
  startDate,
  endDate,
  travelers,
  useCurrentLocation,
  dealType,
  SEMSearch,
  SEMSEARCH,
  mapView,
  selectedTopFilters,
  userLatitude,
  userLongitude,
  refId,
  refClickId,
}: HOTEL_FORM_SUBMIT_TYPE & {
  useCurrentLocation?: boolean
  userLatitude?: number
  userLongitude?: number
}) {
  if (endLocation === null) {
    return
  }
  const { type: searchType } = endLocation
  const updatedSearchType = searchType === 'HOTEL' ? 'CITY' : searchType
  const location =
    (searchType === 'HOTEL' ? endLocation.cityID : endLocation.id) || encodeURIComponent(endLocation.itemName)

  const dealTypeParam = dealType === 'EXPRESS_DEAL' || dealType === 'NHA' ? dealTypeUrlMap[dealType] : ''

  const multiOcc = getHotelMultiOccParam({
    adultCount: travelers.adults,
    childrenAges: travelers.childrenAges,
  })
  const queries = []

  if (searchType === 'NEIGHBOURHOOD' && endLocation.id) {
    queries.push(`clusters=${endLocation.id}`)
  }

  if (searchType === 'HOTEL' && !dealTypeParam) {
    queries.push(`preferredhotelids=${endLocation.id}`)
  }
  if (SEMSearch) {
    queries.push(`SEMSearch=${SEMSearch}`)
  }
  if (SEMSEARCH) {
    queries.push(`SEMSEARCH=${SEMSEARCH}`)
  }

  ;(Object.keys(selectedTopFilters) as Array<FilterCategoryHotel>).forEach((key) => {
    const filters = Array.from(selectedTopFilters[key])
    if (filters.length > 0) {
      const codeFilters = filters.map((filter) => filter.split('_')[1])
      const filterParams = handleFilterParams(key, codeFilters)
      queries.push(filterParams)
    }
  })

  if (refId?.length && refClickId?.length) {
    queries.push(`refid=${refId}`)
    queries.push(`refclickid=${refClickId}`)
  }

  const query = queries.length ? `?${queries.join('&')}` : ''

  const urlDatePart = startDate && endDate ? `/from/${startDate}/to/${endDate}` : ''

  const mapViewPath = mapView ? 'mapview/' : ''
  if (useCurrentLocation && endLocation && userLatitude && userLongitude) {
    return `/relax/geo/${endLocation.itemName}/${userLatitude}/${userLongitude}/from/${startDate}/to/${endDate}/rooms/${travelers.rooms}${multiOcc}${query}`
  }
  if (updatedSearchType === 'POI' || updatedSearchType === 'AIRPORT') {
    return `/relax/${mapViewPath}near/${updatedSearchType.toLowerCase()}/${location}${urlDatePart}/rooms/${
      travelers.rooms
    }${dealTypeParam}${multiOcc}${query}`
  }
  const locationPath =
    searchType === 'NEIGHBOURHOOD' ? endLocation.cityID || encodeURIComponent(endLocation.itemName) : location

  return `/relax/${mapViewPath}in/${locationPath}${urlDatePart}/rooms/${travelers.rooms}${dealTypeParam}${multiOcc}${query}`
}

export function handleSubmitForHotels(values: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) {
  const { startDate, endDate, endLocation, tripType, startLocation, travelers } = values

  const multiOccFields = {
    adultCount: travelers.adults,
    childrenCount: travelers.children,
    childrenAges: travelers.childrenAges,
  }
  const isPackageSearch = tripType.includes(FLIGHT) || tripType.includes(CAR)
  const hasFlight = tripType.includes(FLIGHT)

  if (isPackageSearch) {
    const basePackageSearchDetails = {
      roomCount: travelers.rooms,
      startDate,
      endDate,
      endLocationObject: endLocation,
      tripType,
      isPartialStayDateValid: false,
      ...multiOccFields,
    }
    const flightSearchDetails = {
      startLocationObject: startLocation,
      ...multiOccFields,
    }

    const packageSearchDetails = {
      ...basePackageSearchDetails,
      ...(hasFlight ? flightSearchDetails : {}),
    }
    packagesSearch(packageSearchDetails)
  } else {
    const url = formatHotelsURL(values)
    fireHotelSubmitEvent(initialState, values, pageName)
    if (url) {
      window.open(url)
    }
  }
  void persistSearchData({ key: HOTEL_SEARCH_DATA, recentSearchData: values })
}

export const handleSubmitForExpressDeals = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.EXPRESS_DEAL,
  })
  window.open(url)
}

export const handleSubmitForNHA = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.NHA,
  })
  window.open(url)
}

export const formatShopSearchLink = ({
  tripType,
  startLocation,
  endLocation,
  startDate,
  endDate,
  travelers,
}: HOTEL_FORM_STATE_TYPE) => {
  const stringifiedQuery = queryString.stringify({
    'flight-code': 'RT',
    'trip-type': tripType,
    'origin-item': startLocation?.itemName,
    'origin-id': startLocation?.id,
    'origin-type': startLocation?.type,
    'origin-city': startLocation?.cityName,
    'origin-state': startLocation?.provinceName,
    'origin-state-code': startLocation?.stateCode,
    'origin-country': startLocation?.country,
    'origin-country-code': startLocation?.countryCode,
    'destination-item': endLocation?.itemName,
    'destination-id': endLocation?.id,
    'destination-type': endLocation?.type,
    'destination-city': endLocation?.cityName,
    'destination-state': endLocation?.provinceName,
    'destination-state-code': endLocation?.stateCode,
    'destination-country': endLocation?.country,
    'destination-country-code': endLocation?.countryCode,
    start: startDate,
    end: endDate,
    rooms: travelers?.rooms,
    adults: travelers?.adults,
    children: travelers?.children,
    ages: JSON.stringify(travelers?.childrenAges),
    'hotel-start': startDate,
    'hotel-end': endDate,
    openMultiHotel: true,
  })

  return `/shop/search/trips/?${stringifiedQuery}`
}
